import React, { useState } from 'react';
import { BACKEND_URL } from '../config';
import { useNavigate, Link } from 'react-router-dom';
import { motion } from 'motion/react';
import { toast } from 'sonner';
import LandingHeader from '../components/LandingHeader';
import Footer from '../components/Footer';
import SEO from '../components/SEO';

import {
  ArrowLeft,
  CheckCircle,
  Envelope,
  PaperPlaneRight
} from '@phosphor-icons/react';
const ForgotPassword = () => {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [emailSent, setEmailSent] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!email.trim()) {
      toast.error('Please enter your email address');
      return;
    }

    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      toast.error('Please enter a valid email address');
      return;
    }

    setIsSubmitting(true);

    try {
      const response = await fetch(`${BACKEND_URL}/api/auth/forgot-password`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ email: email.toLowerCase() })
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.detail || 'Failed to send reset email');
      }

      setEmailSent(true);
      toast.success('Password reset email sent! Check your inbox.');

    } catch (error) {
      toast.error(error.message || 'Failed to send reset email. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#161B26]">
      <SEO 
        title="Forgot Password - Man & Machine AI Opportunity Analyzer"
        description="Reset your password to regain access to your account"
      />
      <LandingHeader />

      <div className="pt-32 pb-20">
        <div className="max-w-md mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="glass rounded-3xl p-8"
          >
            {!emailSent ? (
              <>
                {/* Header */}
                <div className="text-center mb-8">
                  <div className="w-16 h-16 rounded-full bg-[rgba(99,102,241,0.1)] flex items-center justify-center mx-auto mb-4">
                    <Envelope size={32} className="text-[#6366F1]" />
                  </div>
                  <h1 className="text-3xl font-bold text-white mb-2">
                    Forgot Password?
                  </h1>
                  <p className="text-[#A0AEC0]">
                    No worries! Enter your email and we'll send you reset instructions.
                  </p>
                </div>

                {/* Form */}
                <form onSubmit={handleSubmit} className="space-y-6">
                  <div>
                    <label className="block text-sm font-medium text-white mb-2">
                      Email Address
                    </label>
                    <div className="relative">
                      <Envelope size={20} className="absolute left-4 top-1/2 -translate-y-1/2 text-[#718096]" />
                      <input
                        type="email"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        className="w-full pl-12 pr-4 py-3 rounded-xl glass text-white focus:outline-none focus:ring-2 focus:ring-[#6366F1] transition-all"
                        placeholder="you@company.com"
                        disabled={isSubmitting}
                      />
                    </div>
                  </div>

                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="w-full rounded-full bg-[#6366F1] hover:bg-[#5558E6] disabled:opacity-50 transition-colors"
                  >
                    <span className="flex items-center justify-center gap-2 py-4 text-white font-bold text-lg">
                      {isSubmitting ? (
                        <>
                          <PaperPlaneRight size={20} className="animate-pulse" />
                          Sending...
                        </>
                      ) : (
                        <>
                          <PaperPlaneRight size={20} />
                          Send Reset Link
                        </>
                      )}
                    </span>
                  </button>
                </form>

                {/* Back to Login */}
                <div className="mt-6 text-center">
                  <Link
                    to="/login"
                    className="inline-flex items-center gap-2 text-[#6366F1] hover:text-[#8B5CF6] transition-colors text-sm font-medium"
                  >
                    <ArrowLeft size={16} />
                    Back to Login
                  </Link>
                </div>
              </>
            ) : (
              <>
                {/* Success State */}
                <div className="text-center">
                  <div className="w-16 h-16 rounded-full bg-[rgba(20,241,149,0.1)] flex items-center justify-center mx-auto mb-4">
                    <CheckCircle size={32} className="text-[#14F195]" />
                  </div>
                  <h2 className="text-2xl font-bold text-white mb-3">
                    Check Your Email
                  </h2>
                  <p className="text-[#A0AEC0] mb-2">
                    We've sent password reset instructions to:
                  </p>
                  <p className="text-white font-semibold mb-6">
                    {email}
                  </p>

                  <div className="space-y-3 mb-8 text-left">
                    <div className="flex items-start gap-3 text-[#A0AEC0] text-sm">
                      <CheckCircle size={20} className="text-[#14F195] flex-shrink-0 mt-0.5" />
                      <span>Click the reset link in your email</span>
                    </div>
                    <div className="flex items-start gap-3 text-[#A0AEC0] text-sm">
                      <CheckCircle size={20} className="text-[#14F195] flex-shrink-0 mt-0.5" />
                      <span>The link will expire in 1 hour</span>
                    </div>
                    <div className="flex items-start gap-3 text-[#A0AEC0] text-sm">
                      <CheckCircle size={20} className="text-[#14F195] flex-shrink-0 mt-0.5" />
                      <span>Check your spam folder if you don't see it</span>
                    </div>
                  </div>

                  <button
                    onClick={() => setEmailSent(false)}
                    className="w-full py-3 rounded-full glass hover:bg-[rgba(255,255,255,0.05)] text-white font-semibold transition-all"
                  >
                    Resend Email
                  </button>

                  <div className="mt-4">
                    <Link
                      to="/login"
                      className="inline-flex items-center gap-2 text-[#6366F1] hover:text-[#8B5CF6] transition-colors text-sm font-medium"
                    >
                      <ArrowLeft size={16} />
                      Back to Login
                    </Link>
                  </div>
                </div>
              </>
            )}
          </motion.div>

          {/* Back to Home */}
          <div className="text-center mt-6">
            <button
              onClick={() => navigate('/')}
              className="text-[#A0AEC0] hover:text-white transition-colors text-sm"
            >
              ← Back to Home
            </button>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default ForgotPassword;
